import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Container,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
} from "@mui/material";
import SelectEntity from "components/SelectEntity";
import useGlobalContext from "hooks/globalData";
import useListTitle from "hooks/useListTitle";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import {
  AdminListRequestOfBaseFilter,
  ListResponseOfSettingGroup,
  ListResponseOfSettingType,
  SettingInheritance,
} from "services/KnowfullyAdminClient";
import persistanceStore from "stores/persistanceStore";
import SettingsTable from "./SettingsTable";

const SettingsList = () => {
  const { formatMessage: t } = useIntl();
  const { settingService } = useGlobalContext();

  const searchTerm = persistanceStore.useState(
    (s) => s.settingsFilterSearchTerm
  );

  const groupSettingIdStored = persistanceStore.useState(
    (s) => s.settingGroupId
  );

  const settingTypeIdStored = persistanceStore.useState((s) => s.settingTypeId);

  const inheritanceStored = persistanceStore.useState(
    (s) => s.settingsInheritance
  );

  useListTitle("settings");

  const fetchedGroupTypes = useQuery<ListResponseOfSettingGroup>(
    ["fetchedGroupTypes"],
    () =>
      settingService.getSettingGroups(
        AdminListRequestOfBaseFilter.fromJS({
          sortField: "name",
        })
      )
  );

  const fetchedSettingTypes = useQuery<ListResponseOfSettingType>(
    ["fetchedSettingTypes"],
    () =>
      settingService.getSettingTypes(
        AdminListRequestOfBaseFilter.fromJS({
          sortField: "name",
        })
      )
  );

  return (
    <Container>
      <Box mb={12}>
        <Grid container spacing={2}>
          <Grid item sm={12} md={12}>
            <TextField
              fullWidth
              variant="outlined"
              value={searchTerm ?? ""}
              placeholder={t({ id: "searchPlaceholder" })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        persistanceStore.update((s) => {
                          s.settingsFilterSearchTerm = "";
                        })
                      }
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onChange={(e) =>
                persistanceStore.update((s) => {
                  s.settingsFilterSearchTerm = e.target.value;
                })
              }
            />
          </Grid>
          <Grid item sm={12} md={4}>
            <SelectEntity
              value={groupSettingIdStored ?? ""}
              fetchedData={fetchedGroupTypes}
              label={t({ id: "groupLabel" })}
              onChange={(id) =>
                persistanceStore.update((s) => {
                  s.settingGroupId = id!;
                })
              }
              textFieldProps={{
                variant: "outlined",
              }}
              labelField={"name"}
            />
          </Grid>
          <Grid item sm={12} md={4}>
            <SelectEntity
              value={settingTypeIdStored ?? ""}
              fetchedData={fetchedSettingTypes}
              label={t({ id: "typeLabel" })}
              onChange={(id) =>
                persistanceStore.update((s) => {
                  s.settingTypeId = id!;
                })
              }
              textFieldProps={{
                variant: "outlined",
              }}
              labelField={"name"}
            />
          </Grid>
          <Grid item sm={12} md={4}>
            <TextField
              select
              fullWidth
              variant="outlined"
              label={t({ id: "settingInheritanceLabel" })}
              value={inheritanceStored ?? ""}
              // Without displayEmpty the Select renders nothing for the empty "All" option.
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
              onChange={(e) =>
                persistanceStore.update((s) => {
                  s.settingsInheritance =
                    (e.target.value as SettingInheritance) || undefined;
                })
              }
            >
              <MenuItem value="">{t({ id: "settingInheritanceAll" })}</MenuItem>
              <MenuItem value={SettingInheritance.Overridden}>
                {t({ id: "settingInheritanceOverridden" })}
              </MenuItem>
              <MenuItem value={SettingInheritance.Inherited}>
                {t({ id: "settingInheritanceInherited" })}
              </MenuItem>
            </TextField>
          </Grid>
          <Grid item sm={12}>
            <SettingsTable />
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default SettingsList;
